import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabase'
import { formatCourt, formatLong, parseIso, fetchAll } from '../../utils/planning'
import { genererJours, fermeturesAReprendre, periodesFermees } from '../../utils/planningSaison'

// Proposition par defaut : saison qui suit la derniere (1er aout au 31 juillet)
function propositionDates(saisons) {
  if (saisons.length === 0) return { debut: '', fin: '' }
  const derniere = saisons[0]
  const a = parseIso(derniere.date_fin).annee
  return { debut: `${a}-08-01`, fin: `${a + 1}-07-31` }
}

function libelleDe(debut, fin) {
  if (!debut || !fin) return ''
  const a = parseIso(debut).annee
  const b = parseIso(fin).annee
  return a === b ? String(a) : `${a}-${b}`
}

export default function NouvelleSaison() {
  const [saisons, setSaisons] = useState([])
  const [loading, setLoading] = useState(true)
  const [debut, setDebut] = useState('')
  const [fin, setFin] = useState('')
  const [libelle, setLibelle] = useState('')
  const [libelleModifie, setLibelleModifie] = useState(false)
  const [copier, setCopier] = useState(true)
  const [prevJours, setPrevJours] = useState([])
  const [creation, setCreation] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    supabase.from('planning_saisons').select('*').order('date_debut', { ascending: false }).then(({ data, error }) => {
      if (error) setMsg({ type: 'error', text: 'Impossible de charger les saisons.' })
      const liste = data || []
      setSaisons(liste)
      const p = propositionDates(liste)
      setDebut(p.debut)
      setFin(p.fin)
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!libelleModifie) setLibelle(libelleDe(debut, fin))
  }, [debut, fin, libelleModifie])

  // Saison de reference : la plus recente qui se termine avant le debut de la nouvelle
  const reference = useMemo(
    () => saisons.find(s => debut && s.date_fin < debut) || null,
    [saisons, debut]
  )

  useEffect(() => {
    let annule = false
    if (!reference) { setPrevJours([]); return undefined }
    fetchAll(() => supabase.from('jours_planning')
      .select('date, accueil_ouvert, supervision_ouverte, libelle')
      .gte('date', reference.date_debut).lte('date', reference.date_fin).order('date'))
      .then(({ data }) => { if (!annule) setPrevJours(data) })
    return () => { annule = true }
  }, [reference])

  const valide = debut && fin && fin > debut
  const jours = useMemo(() => {
    if (!valide) return []
    const reprises = copier && prevJours.length > 0 ? fermeturesAReprendre(prevJours) : null
    return genererJours(debut, fin, reprises)
  }, [valide, debut, fin, copier, prevJours])

  const feries = jours.filter(j => j.ferie)
  const periodes = periodesFermees(jours)

  const chevauche = saisons.find(s => debut <= s.date_fin && fin >= s.date_debut)
  const doublon = saisons.find(s => s.libelle === libelle.trim())

  async function creer() {
    setMsg(null)
    if (!valide) { setMsg({ type: 'error', text: 'Dates invalides.' }); return }
    if (!libelle.trim()) { setMsg({ type: 'error', text: 'Donne un nom à la saison.' }); return }
    if (chevauche) { setMsg({ type: 'error', text: `Ces dates chevauchent la saison ${chevauche.libelle}.` }); return }
    if (doublon) { setMsg({ type: 'error', text: 'Une saison porte déjà ce nom.' }); return }
    setCreation(true)
    const { data: saison, error } = await supabase.from('planning_saisons')
      .insert({ libelle: libelle.trim(), date_debut: debut, date_fin: fin, statut: 'construction' })
      .select().single()
    if (error) {
      setCreation(false)
      setMsg({ type: 'error', text: 'Impossible de créer la saison.' })
      return
    }
    const lignes = jours.map(j => ({ ...j, saison_id: saison.id }))
    let echec = false
    for (let i = 0; i < lignes.length && !echec; i += 200) {
      const { error: e } = await supabase.from('jours_planning').insert(lignes.slice(i, i + 200))
      if (e) echec = true
    }
    if (echec) {
      // on ne laisse pas une saison a moitie remplie
      await supabase.from('jours_planning').delete().eq('saison_id', saison.id)
      await supabase.from('planning_saisons').delete().eq('id', saison.id)
      setCreation(false)
      setMsg({ type: 'error', text: "Erreur pendant la création du calendrier. Rien n'a été créé." })
      return
    }
    setCreation(false)
    setSaisons(prev => [saison, ...prev])
    setMsg({
      type: 'success',
      text: `Saison ${saison.libelle} créée (${lignes.length} jours). Ajuste le calendrier dans « Calendrier d'ouverture », puis ouvre la collecte dans « Campagne de dispos ».`,
    })
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Nouvelle saison</h2>
          <div className="page-header-sub">Génère le calendrier d'une saison : jours fériés et fermetures de vacances.</div>
        </div>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 20px' }}>
          <div className="form-group">
            <label>Début de saison</label>
            <input type="date" value={debut} onChange={e => setDebut(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Fin de saison</label>
            <input type="date" value={fin} onChange={e => setFin(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Nom</label>
            <input value={libelle} onChange={e => { setLibelle(e.target.value); setLibelleModifie(true) }} />
          </div>
        </div>
        {reference && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12 }}>
            <input type="checkbox" checked={copier} onChange={e => setCopier(e.target.checked)} />
            Reprendre les périodes de fermeture de la saison {reference.libelle} (vacances, à ajuster ensuite)
          </label>
        )}
        {chevauche && <div className="alert alert-warning">Ces dates chevauchent la saison {chevauche.libelle}.</div>}
        {!chevauche && doublon && <div className="alert alert-warning">Une saison porte déjà ce nom.</div>}
        <button className="btn btn-bordeaux" onClick={creer} disabled={creation || !valide || !!chevauche || !!doublon}>
          {creation ? 'Création...' : 'Créer la saison'}
        </button>
        <div className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
          La saison est créée à l'étape « Construction du planning » : les superviseurs ne peuvent pas encore saisir de disponibilités.
          Par défaut, accueil et supervision sont ouverts tous les jours, l'hydro est fermée, les jours fériés sont fermés.
        </div>
      </div>

      {valide && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          <div className="card">
            <h3 style={{ marginBottom: 10 }}>Jours fériés ({feries.length})</h3>
            <table>
              <tbody>
                {feries.map(j => (
                  <tr key={j.date}>
                    <td style={{ whiteSpace: 'nowrap' }}><strong>{formatCourt(j.date)}</strong></td>
                    <td className="text-muted">{j.libelle.replace('Jour férié : ', '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card">
            <h3 style={{ marginBottom: 10 }}>Fermetures reprises ({periodes.length})</h3>
            {periodes.length === 0 ? (
              <div className="text-muted">Aucune. Utilise « Modifier une période » dans le calendrier d'ouverture.</div>
            ) : (
              <table>
                <tbody>
                  {periodes.map(p => (
                    <tr key={p.debut}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {p.debut === p.fin ? formatLong(p.debut) : `${formatLong(p.debut)} au ${formatLong(p.fin)}`}
                      </td>
                      <td className="text-muted">{p.libelle || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
